// src/components/list/SensorSelector.tsx

import {
  ArrowDropDownTwoTone,
  ArrowDropUpTwoTone,
  HistoryTwoTone,
  Star,
} from '@mui/icons-material'
import {
  Box,
  Checkbox,
  Chip,
  Collapse,
  FormControlLabel,
  FormGroup,
  Input,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Skeleton,
  Theme,
  Typography,
  useTheme,
} from '@mui/material'
import { useEffect, useState } from 'react'

import {
  DataSamplingDate,
  FolderId,
  FolderName,
  MeterNumber,
  SamplingMode,
  SensorId,
  SensorName,
  SensorPool,
} from '@/types/domain'

import {
  HStack,
  ToggleOrder,
  ToggleSelectedOnly,
  ToggleShowSensorInfo,
} from '..'
import { ASC, DDMMYYYY, DESC, userLocales as locales } from '../../api'
import {
  OperatorData,
  useAppConfig,
  useDataSamplingDate,
  useFolderName,
  useMeterNumber,
  useOperator,
  useParentId,
  useSamplingMode,
  useSensorName,
  useSensorPool,
} from '../../hooks'

const COL_SELECTED = '#f4bcbc !important'

const FILTER_NOT_SUCCESSOR_SENSOR = f => f.successor_id === null

type SubItemProps = {
  lastItem: boolean
  successor_id: SensorId
  border?: string
  spacing?: number
}

const SubItem = ({
  lastItem,
  successor_id,
  border = '1px solid #CCC',
  spacing = 0,
}: SubItemProps) => (
  <Box sx={{ mr: spacing }}>
    <Box
      sx={{
        position: 'absolute',
        left: 28,
        borderLeft: border,
        top: 0,
        height: lastItem ? '51%' : '100%',
      }}
    />

    {spacing === 0 && (
      <Box
        sx={{
          position: 'absolute',
          left: successor_id === null ? 28 : 50,
          top: '50%',
          width: 10,
          borderBottom: border,
          transform: 'translateY(-50%)',
        }}
      />
    )}
  </Box>
)

type ChippedSensorNameProps = {
  name: SensorName
}

const ChippedSensorName = ({ name }: ChippedSensorNameProps) => {
  const match = name?.match(/\(bis[^)]+\)/)

  if (match) {
    const chipText = match[0].replaceAll(/[()]/g, '').trim()
    const baseText = name?.replace(match[0], '').trim()

    return (
      <>
        {baseText}&nbsp;
        <Chip label={chipText} size={'small'} />
      </>
    )
  }

  return name
}

type SensorInfoProps = {
  id: SensorId
  customFields: object | null
}

const SensorInfo = ({ id, customFields }: SensorInfoProps) => {
  const opData: OperatorData = useOperator()
  const isAdmin = opData?.isAdmin
  const meter: MeterNumber = useMeterNumber(id)
  const sMode: SamplingMode = useSamplingMode(id)
  const sampling: string =
    sMode === 1
      ? 'Zählerstand'
      : sMode === 2
        ? 'Verbrauch'
        : sMode === 3
          ? 'Import'
          : '-'
  const dsd: DataSamplingDate = useDataSamplingDate(id)
  const firstRecord: string = new Date(dsd).toLocaleDateString(
    locales,
    DDMMYYYY
  )

  return (
    <Box>
      <Box>Zählernummer: {meter}</Box>
      <Box>Datenbeginn: {firstRecord}</Box>
      <Box>Messverfahren: {sampling}</Box>

      {customFields &&
        Object.entries(customFields).map(
          ([key, custom_field]) =>
            custom_field.is_visible_in_performance_details && (
              <Box key={key}>
                {custom_field.label}: {custom_field.value}
              </Box>
            )
        )}

      {isAdmin && <Box>Sensor ID: {id}</Box>}
    </Box>
  )
}

type SuccessorItemProps = {
  color: any
  custom_fields: object | null
  id: SensorId
  lastItem: boolean
  selected: boolean
  onClick: () => void
  showInfo: boolean
  successor_id: SensorId
  fromDate: string
}

const SuccessorItem = ({
  color,
  custom_fields,
  id,
  lastItem,
  selected,
  onClick,
  showInfo,
  successor_id,
  fromDate,
}: SuccessorItemProps) => {
  const name: SensorName = useSensorName(id)
  const { appConfig } = useAppConfig()

  const parentId: SensorId = useParentId(id)
  const isSub: boolean = parentId !== null
  const dsd: DataSamplingDate = useDataSamplingDate(id)
  const hasNoData: boolean =
    new Date(dsd).getTime() > new Date(fromDate).getTime()

  const backgroundColor =
    appConfig.showMissingSensorDataNotice && hasNoData && selected
      ? COL_SELECTED
      : 'inherit'

  return (
    <ListItemButton
      onClick={onClick}
      selected={selected}
      sx={{ backgroundColor }}
    >
      {isSub && (
        <SubItem
          lastItem={lastItem}
          successor_id={successor_id}
          border={'1px dashed #CCC'}
          spacing={1}
        />
      )}

      <ListItemIcon sx={{ color, ml: isSub ? 2.8 : 0 }}>
        <Star />
      </ListItemIcon>

      <ListItemText
        primary={<ChippedSensorName name={name} />}
        secondary={
          showInfo ? (
            <SensorInfo id={id} customFields={custom_fields} />
          ) : undefined
        }
      />
    </ListItemButton>
  )
}

type SensorItemProps = {
  id: SensorId
  onClick: () => void
  setSelected: any
  handleOpenIDs: any

  options: {
    color: any
    custom_fields: object | null
    data: any[]
    exportModal: boolean
    fromDate: string
    isSelected: boolean
    lastItem: boolean
    openIds: SensorId[]
    order: any
    selected: SensorId[]
    sensorsUnderChart: boolean
    showInfo: boolean
    successor_id: SensorId
    successorData: any[]
  }
}

const SensorItem = ({
  id,
  onClick,
  setSelected,
  handleOpenIDs,
  options,
}: SensorItemProps) => {
  const {
    color,
    custom_fields,
    data,
    exportModal,
    fromDate,
    isSelected,
    lastItem,
    openIds,
    order,
    selected,
    sensorsUnderChart,
    showInfo,
    successor_id,
    successorData,
  } = options

  const name: SensorName = useSensorName(id)
  const { appConfig } = useAppConfig()

  const onSuccessorClick = event => {
    event.stopPropagation()
    handleOpenIDs(id)
  }

  const parentId: SensorId = useParentId(id)
  const isSub: boolean = parentId !== null
  const dsd: DataSamplingDate = useDataSamplingDate(id)
  const hasNoData: boolean =
    new Date(dsd).getTime() > new Date(fromDate).getTime()

  return (
    <ListItem
      sx={{
        p: 0,

        ...(exportModal && sensorsUnderChart
          ? {
              // max 3 nebeneinander, gleich breit
              flexBasis: 'calc((100% - 16px) / 3)',
              maxWidth: 'calc((100% - 16px) / 3)',
              flexGrow: 0,
              display: 'block',
            }
          : {
              width: '100%',
              display: 'block',
            }),
      }}
    >
      <ListItemButton
        onClick={onClick}
        selected={isSelected}
        sx={{
          backgroundColor:
            appConfig.showMissingSensorDataNotice &&
            hasNoData &&
            selected.includes(id)
              ? COL_SELECTED
              : 'inherit',
        }}
      >
        {isSub && <SubItem lastItem={lastItem} successor_id={successor_id} />}

        <ListItemIcon sx={{ color, ml: isSub ? 2.8 : 0 }}>
          <Star />
        </ListItemIcon>

        <ListItemText
          primary={name}
          secondary={
            showInfo ? (
              <SensorInfo id={id} customFields={custom_fields} />
            ) : undefined
          }
        />

        {successorData.length > 0 && (
          <HistoryTwoTone onClick={onSuccessorClick} />
        )}

        {successorData.length > 0 &&
          (openIds.includes(id) ? (
            <ArrowDropUpTwoTone onClick={onSuccessorClick} />
          ) : (
            <ArrowDropDownTwoTone onClick={onSuccessorClick} />
          ))}
      </ListItemButton>

      {/* successor sensors */}
      {successorData.length > 0 && (
        <Collapse in={openIds.includes(id)}>
          {successorData.map(sensor => {
            const { chartColor, entity_id } = sensor

            const isSelected = selected.includes(entity_id)

            const successorToggle = () => {
              console.log('toggle successor', entity_id)
              const newValue = isSelected
                ? selected.filter(f => f !== entity_id)
                : [...selected, entity_id]

              setSelected(
                data
                  .sort((a, b) => {
                    const result = a.name.localeCompare(b.name)

                    return order === ASC ? result : -result
                  })
                  .map(s => s.entity_id)
                  .filter(f => newValue.includes(f))
              )
            }

            return (
              <SuccessorItem
                color={chartColor}
                custom_fields={custom_fields}
                fromDate={fromDate}
                id={entity_id}
                lastItem={lastItem}
                selected={isSelected}
                key={entity_id}
                onClick={successorToggle}
                showInfo={showInfo}
                successor_id={successor_id}
              />
            )
          })}
        </Collapse>
      )}
    </ListItem>
  )
}

type SensorSelectorProps = {
  border?: string
  id: FolderId
  onChange: (selected: SensorId[]) => void
  onChangeOrder: (order: string) => void
  onChangeInfo: (show: boolean) => void
  options: {
    data: any[]
    exportModal: boolean
    fromDate: string
    medium: string
    order: string
    selected: SensorId[]
    sensorsUnderChart: boolean
  }
}

export const SensorSelector = ({
  border = 'solid 1px #DDD',
  id,
  onChange,
  onChangeOrder,
  onChangeInfo,
  options,
}: SensorSelectorProps) => {
  const {
    data,
    exportModal,
    fromDate,
    medium,
    order: initialOrder,
    selected: initialSelected,
    sensorsUnderChart,
  } = options || {}

  const sPool: SensorPool = useSensorPool()
  const fName: FolderName = useFolderName(id)

  const theme: Theme = useTheme<Theme>()
  const { white: bgcolor } = theme.palette

  const [order, setOrder] = useState<string>(initialOrder)
  const [search, setSearch] = useState<string>('')
  const [selectedOnly, setSelectedOnly] = useState<boolean>(false)
  const [selected, setSelected] = useState<SensorId[]>(initialSelected)
  const [showInfo, setShowInfo] = useState<boolean>(false)
  const [openIds, setOpenIds] = useState<SensorId[]>([])

  const indeterminate: boolean =
    selected.length > 0 && selected.length !== data.length

  const fixedHeight: number =
    exportModal && sensorsUnderChart
      ? (showInfo ? 150 : 75) * selected.length
      : 645

  const FILTER_SEARCH = f =>
    f.name.toLowerCase().includes(search.toLowerCase()) ||
    (f.meter_number &&
      f.meter_number.toLowerCase().includes(search.toLowerCase()))

  const FILTER_SELECTED_ONLY = f =>
    selectedOnly === true || exportModal === true
      ? selected.includes(f.entity_id)
      : f

  const FILTER_SELECTED_SUCCESSOR_SENSOR = f => {
    const sensor = data.find(s => s.entity_id === f)
    return sensor ? sensor.successor_id === null : false
  }

  const SORT_HANDLER = (a, b) => {
    const result = a.name.localeCompare(b.name)

    return order === ASC ? result : -result
  }

  const getPredecessors = data ? data.filter(f => f.successor_id !== null) : []

  const getSuccessor = (id: SensorId) =>
    data
      .filter(FILTER_SELECTED_ONLY)
      .filter(FILTER_SEARCH)
      .filter(f => f.successor_id === id)
      .sort(
        (a, b) =>
          new Date(b.data_sampling_date).getTime() -
          new Date(a.data_sampling_date).getTime()
      )

  const handleKeyPress = event => {
    // deselect all // 109 = NumPad-; 189 = -;
    if (event.keyCode === 109 || event.keyCode === 189) setSelected([])

    // select all; 107 = NumPad+; 187 = +;
    if (event.keyCode === 107 || event.keyCode === 187)
      setSelected(data.sort(SORT_HANDLER).map(item => item.entity_id))
  }

  const handleOpenIDs = (id: SensorId) =>
    setOpenIds(prev =>
      prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]
    )

  const handleToggleAll = () => {
    if (selected.length !== data.length)
      setSelected(data.sort(SORT_HANDLER).map(item => item.entity_id))
    else setSelected([])
  }

  const sensorSortFn = (a, b, data) => {
    const getKey = s => {
      const parent = data.find(p => p.entity_id === s.parent_id)
      const parentName = parent ? parent.name : s.name
      const isMain = s.parent_id === null ? '0' : '1'

      return [parentName, isMain, s.name]
    }

    const keyA = getKey(a).join('|')
    const keyB = getKey(b).join('|')
    const comparison = keyA.localeCompare(keyB, 'de', { sensitivity: 'base' })

    return order === DESC ? -comparison : comparison
  }

  document.onkeydown = handleKeyPress

  useEffect(() => onChangeOrder(order), [onChangeOrder, order])
  useEffect(() => onChange(selected), [onChange, order, selected])
  useEffect(() => setSelected([]), [medium])
  useEffect(() => {
    if (onChangeInfo) onChangeInfo(showInfo)
  }, [onChangeInfo, showInfo])

  const onSetFilter = event => setSearch(event.target.value)
  const onSetOrder = order => setOrder(order)
  const toggleSelectedOnly = only => setSelectedOnly(only)
  const toggleSensorInfo = show => setShowInfo(show)

  const isExportUnderChart = exportModal && sensorsUnderChart

  return sPool.length > 0 ? (
    <Collapse in={data.length > 0}>
      <Collapse in={!exportModal}>
        <HStack spacing={0} sx={{ borderBottom: border, borderLeft: border }}>
          <Typography
            sx={{
              borderRight: border,
              fontSize: '0.75rem',
              px: 1,
              py: 0,
            }}
          >
            Messpunkte (
            {selected.filter(FILTER_SELECTED_SUCCESSOR_SENSOR).length}/
            {data.filter(FILTER_NOT_SUCCESSOR_SENSOR).length})
          </Typography>

          <Input
            onChange={onSetFilter}
            placeholder={'Filter ...'}
            sx={{ m: 0, minHeight: 23, pl: 1, width: 1 }}
            value={search}
          />

          <ToggleOrder order={order} onChange={onSetOrder} />
          <ToggleSelectedOnly onChange={toggleSelectedOnly} />
          <ToggleShowSensorInfo onChange={toggleSensorInfo} />
        </HStack>
      </Collapse>

      <List
        sx={{
          bgcolor,
          borderLeft: 'solid 1px #D3D3D3',
          ...(isExportUnderChart
            ? { height: 'auto' }
            : {
                height: fixedHeight,
                minHeight: fixedHeight,
                maxHeight: fixedHeight,
              }),
          overflow: 'auto',
          pt: 0,
          width: 1,

          ...(isExportUnderChart && {
            display: 'flex',
            flexWrap: 'wrap',
            alignContent: 'flex-start',
          }),
        }}
      >
        <Collapse
          in={exportModal}
          sx={{ width: '100%', flexBasis: '100%', display: 'block' }}
        >
          <ListSubheader
            sx={{
              bgcolor: '#EEE',
              borderBottom: 'solid 1px #D3D3D3',
              flexBasis: '100%',
            }}
          >
            {fName}
          </ListSubheader>
        </Collapse>

        {!exportModal && (
          <Collapse in={!exportModal}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selected.length === data.length}
                    indeterminate={indeterminate}
                    onChange={handleToggleAll}
                    sx={{ ml: 1.8, mr: 2.2 }}
                  />
                }
                label={'Alle auswählen'}
              />
            </FormGroup>
          </Collapse>
        )}

        {/* Sensor List */}
        {data
          .filter(FILTER_SELECTED_ONLY)
          .sort(SORT_HANDLER)
          .sort((a, b) => sensorSortFn(a, b, data))
          .map(sensor => {
            const {
              chartColor,
              entity_id,
              name,
              meter_number,
              parent_id,
              successor_id,
              custom_fields,
            } = sensor

            const hasPredecessor = getPredecessors.some(
              f => f.entity_id === entity_id
            )

            const successorData = getSuccessor(entity_id)
            const isSelected: boolean = selected.includes(entity_id)

            const sameParent = data.filter(f => f.parent_id === parent_id)
            const lastItem: boolean =
              parent_id !== null && sameParent.at(-1).entity_id === entity_id

            const toggle = () => {
              console.log('toggle', entity_id)
              const newValue = isSelected
                ? selected.filter(f => f !== entity_id)
                : [...selected, entity_id]

              setSelected(
                data
                  .sort(SORT_HANDLER)
                  .map(s => s.entity_id)
                  .filter(f => newValue.includes(f))
              )
            }

            if (hasPredecessor) return null

            if (selectedOnly && !isSelected && successorData.length === 0)
              return null

            return name.toLowerCase().includes(search.toLowerCase()) ||
              meter_number?.toLowerCase()?.includes(search.toLowerCase()) ||
              successorData.length > 0 ? (
              <SensorItem
                key={entity_id}
                id={entity_id}
                onClick={toggle}
                setSelected={setSelected}
                handleOpenIDs={handleOpenIDs}
                options={{
                  color: chartColor,
                  custom_fields,
                  data,
                  exportModal,
                  fromDate,
                  isSelected,
                  lastItem,
                  openIds,
                  order,
                  selected,
                  sensorsUnderChart,
                  showInfo,
                  successor_id,
                  successorData,
                }}
              />
            ) : null
          })}
      </List>
    </Collapse>
  ) : (
    <Skeleton
      variant={'rectangular'}
      width={'100%'}
      height={650}
      sx={{ bgcolor: '#EEE' }}
    />
  )
}
